/**
 * Visitor Identity Manager
 *
 * Manages HubSpot Visitor Identification for the chatflow widget.
 * When a visitor provides their email (e.g., design save form), their identity
 * is cached in localStorage. On subsequent page loads, a fresh HubSpot
 * identification token is fetched (with a 6-hour TTL cache) and passed to
 * the chat widget so it recognizes the visitor without asking for email.
 *
 * Flow:
 * 1. Design save → storeVisitorIdentity(email, firstName) writes to localStorage
 * 2. Page load → init() checks localStorage for stored identity
 * 3. If identity exists + token expired → fetch fresh token from Bliss → cache it
 * 4. If identity exists + token fresh → use cached token
 * 5. Pass identificationEmail + identificationToken to hsConversationsSettings
 * 6. Load/reload the HubSpot chat widget
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'studio_home_visitor_identity';
  var TOKEN_TTL_MS = 6 * 60 * 60 * 1000; // 6 hours
  var TOKEN_ENDPOINT = (window.STUDIO_SHED_DESIGN_API_ENDPOINT || 'https://design.studio-shed.com/api/designs')
    .replace('/api/designs', '/api/hubspot/visitor-token');

  /**
   * Store visitor identity after form submission (design save, consultation, etc.)
   * Called from configurator-shared.js after successful design save.
   */
  function storeVisitorIdentity(email, firstName, lastName) {
    if (!email) return;

    var existing = getStoredIdentity();
    var identity = {
      email: email.trim(),
      firstName: (firstName || '').trim(),
      lastName: (lastName || '').trim(),
      token: existing && existing.email === email.trim() ? existing.token : null,
      tokenFetchedAt: existing && existing.email === email.trim() ? existing.tokenFetchedAt : null,
    };

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(identity));
      console.log('[VisitorIdentity] Stored identity for:', identity.email);
    } catch (e) {
      console.warn('[VisitorIdentity] localStorage write failed:', e);
    }
  }

  /**
   * Get stored identity from localStorage.
   */
  function getStoredIdentity() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || !parsed.email) return null;
      return parsed;
    } catch (e) {
      return null;
    }
  }

  /**
   * Check if the cached token is still fresh (within TTL).
   */
  function isTokenFresh(identity) {
    if (!identity || !identity.token || !identity.tokenFetchedAt) return false;
    var age = Date.now() - identity.tokenFetchedAt;
    return age < TOKEN_TTL_MS;
  }

  /**
   * Fetch a fresh identification token from Bliss → HubSpot.
   */
  async function fetchToken(identity) {
    try {
      var response = await fetch(TOKEN_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          email: identity.email,
          firstName: identity.firstName || undefined,
          lastName: identity.lastName || undefined,
        }),
      });

      if (!response.ok) {
        console.warn('[VisitorIdentity] Token fetch failed:', response.status);
        return null;
      }

      var data = await response.json();
      return data.token || null;
    } catch (e) {
      console.warn('[VisitorIdentity] Token fetch error:', e);
      return null;
    }
  }

  /**
   * Apply visitor identity to the HubSpot chat widget.
   *
   * Handles two timing scenarios:
   * 1. Widget already loaded (HubSpot script beat us) → remove + set identity + reload
   * 2. Widget not yet loaded (we beat HubSpot) → set identity + register onReady callback
   *
   * Both paths result in the widget loading with the visitor identified.
   */
  function applyIdentityToChat(email, token) {
    if (!email || !token) return;

    // Always set the identification settings — regardless of widget state
    window.hsConversationsSettings = window.hsConversationsSettings || {};
    window.hsConversationsSettings.identificationEmail = email;
    window.hsConversationsSettings.identificationToken = token;

    if (window.HubSpotConversations && window.HubSpotConversations.widget) {
      // Widget already loaded — it came up anonymous. Remove and reload with identity.
      console.log('[VisitorIdentity] Widget already loaded, reloading with identity');
      window.HubSpotConversations.widget.remove();
      window.HubSpotConversations.widget.load();
    } else {
      // Widget hasn't loaded yet — our settings will be picked up when it does.
      // Register the onReady callback as a safety net.
      console.log('[VisitorIdentity] Widget not yet loaded, identity set for when it arrives');
      window.hsConversationsOnReady = window.hsConversationsOnReady || [];
      window.hsConversationsOnReady.push(function () {
        // Settings are already on window.hsConversationsSettings — just load
        window.HubSpotConversations.widget.load();
        console.log('[VisitorIdentity] Widget loaded via onReady with identity');
      });
    }
  }

  /**
   * Initialize visitor identity on page load.
   * Checks localStorage, refreshes token if needed, passes to chat widget.
   */
  async function init() {
    var identity = getStoredIdentity();
    if (!identity) {
      console.log('[VisitorIdentity] No stored identity, widget loads normally');
      // No action needed — HubSpot widget loads on its own as anonymous
      return;
    }

    console.log('[VisitorIdentity] Found stored identity for:', identity.email);

    var token = identity.token;

    if (!isTokenFresh(identity)) {
      console.log('[VisitorIdentity] Token expired or missing, fetching fresh token');
      token = await fetchToken(identity);

      if (token) {
        // Update localStorage with fresh token
        identity.token = token;
        identity.tokenFetchedAt = Date.now();
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(identity));
        } catch (e) { /* ignore */ }
        console.log('[VisitorIdentity] Fresh token cached');
      } else {
        console.warn('[VisitorIdentity] Could not fetch token, widget loads normally');
        // Don't interfere — let HubSpot load the widget as anonymous
        return;
      }
    } else {
      console.log('[VisitorIdentity] Using cached token (still fresh)');
    }

    applyIdentityToChat(identity.email, token);
  }

  /**
   * Clear stored identity (e.g., if user wants to reset).
   */
  function clearVisitorIdentity() {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) { /* ignore */ }

    if (window.HubSpotConversations && window.HubSpotConversations.widget) {
      window.HubSpotConversations.widget.remove();
      if (window.HubSpotConversations.clear) {
        window.HubSpotConversations.clear({ resetWidget: true });
      }
      delete window.hsConversationsSettings.identificationEmail;
      delete window.hsConversationsSettings.identificationToken;
      window.HubSpotConversations.widget.load();
    }

    console.log('[VisitorIdentity] Identity cleared');
  }

  // Expose public API
  window.VisitorIdentity = {
    store: storeVisitorIdentity,
    clear: clearVisitorIdentity,
    getStored: getStoredIdentity,
    init: init,
  };

  // Auto-init on DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
